import React, {
    useEffect,
    useState,
} from 'react';

import {
    createPortal,
} from 'react-dom';

import {
    useTranslation,
} from 'react-i18next';

import {
    changePassword,
    verifyCurrentPassword,
} from '../../services/profileService';


const PASSWORD_REGEX =
    /^[-a-zA-Z0-9!@#$%^&*()_+[\]{};':"\\|,.<>/?`~]{4,12}$/;


export default function PasswordChangeModal({
    open,
    onClose,
    onChanged,
}) {
    const {
        t,
    } =
        useTranslation(
            'app',
        );

    const [
        currentPassword,
        setCurrentPassword,
    ] =
        useState(
            '',
        );


    const [
        newPassword,
        setNewPassword,
    ] =
        useState(
            '',
        );


    const [
        confirmPassword,
        setConfirmPassword,
    ] =
        useState(
            '',
        );


    const [
        errors,
        setErrors,
    ] =
        useState({});

    const [
        currentPasswordStatus,
        setCurrentPasswordStatus,
    ] =
        useState(
            'idle',
        );


    const [
        isSaving,
        setIsSaving,
    ] =
        useState(
            false,
        );

    const passwordRules = {
        format:
            Boolean(
                newPassword,
            ) &&
            PASSWORD_REGEX.test(
                newPassword,
            ),

        differentFromCurrent:
            Boolean(
                newPassword,
            ) &&
            Boolean(
                currentPassword,
            ) &&
            newPassword !==
            currentPassword,

        confirmation:
            Boolean(
                confirmPassword,
            ) &&
            newPassword ===
            confirmPassword,
    };


    const isNewPasswordValid =
        PASSWORD_REGEX.test(
            newPassword,
        ) &&
        passwordRules.differentFromCurrent;


    const canSave =
        currentPasswordStatus ===
        'valid' &&
        isNewPasswordValid &&
        passwordRules.confirmation &&
        !isSaving;

    useEffect(
        () => {
            if (
                !open
            ) {
                return;
            }

            setCurrentPassword(
                '',
            );

            setNewPassword(
                '',
            );

            setConfirmPassword(
                '',
            );

            setErrors(
                {},
            );

            setCurrentPasswordStatus(
                'idle',
            );

            setIsSaving(
                false,
            );
        },
        [
            open,
        ],
    );


    /*
     * 현재 비밀번호 실시간 확인
     *
     * 매 타이핑마다 API를 바로 호출하지 않고
     * 500ms 동안 입력이 멈추면 확인
     */
    useEffect(
        () => {
            if (
                !open
            ) {
                return undefined;
            }

            const password =
                String(
                    currentPassword ||
                    '',
                );

            if (
                !password
            ) {
                setCurrentPasswordStatus(
                    'idle',
                );

                setErrors(
                    (
                        previous,
                    ) => {
                        const next = {
                            ...previous,
                        };

                        delete next.currentPassword;

                        return next;
                    },
                );

                return undefined;
            }


            let cancelled =
                false;


            setCurrentPasswordStatus(
                'checking',
            );


            setErrors(
                (
                    previous,
                ) => {
                    const next = {
                        ...previous,
                    };

                    delete next.currentPassword;
                    delete next.submit;

                    return next;
                },
            );


            const timer =
                setTimeout(
                    async () => {
                        try {
                            const verified =
                                await verifyCurrentPassword(
                                    password,
                                );

                            if (
                                cancelled
                            ) {
                                return;
                            }

                            if (
                                verified
                            ) {
                                setCurrentPasswordStatus(
                                    'valid',
                                );

                                return;
                            }

                            setCurrentPasswordStatus(
                                'invalid',
                            );

                            setErrors(
                                (
                                    previous,
                                ) => ({
                                    ...previous,

                                    currentPassword:
                                        t(
                                            'memberedit.passwordVerification.mismatch',
                                        ),
                                }),
                            );
                        } catch (
                        error
                        ) {
                            if (
                                cancelled
                            ) {
                                return;
                            }

                            console.error(
                                '[PasswordChangeModal] Failed to verify current password:',
                                error,
                            );

                            setCurrentPasswordStatus(
                                'error',
                            );

                            setErrors(
                                (
                                    previous,
                                ) => ({
                                    ...previous,

                                    currentPassword:
                                        t(
                                            'memberedit.passwordVerification.mismatch'
                                        ),
                                }),
                            );
                        }
                    },
                    500,
                );


            return () => {
                cancelled =
                    true;

                clearTimeout(
                    timer,
                );
            };
        },
        [
            open,
            currentPassword,
            t,
        ],
    );


    const resetModal =
        () => {
            setCurrentPassword(
                '',
            );

            setNewPassword(
                '',
            );

            setConfirmPassword(
                '',
            );

            setErrors(
                {},
            );

            setCurrentPasswordStatus(
                'idle',
            );
        };


    const handleClose =
        () => {
            if (
                isSaving
            ) {
                return;
            }

            resetModal();

            onClose?.();
        };


    const clearSubmitError =
        () => {
            setErrors(
                (
                    previous,
                ) => {
                    const next = {
                        ...previous,
                    };

                    delete next.submit;

                    return next;
                },
            );
        };


    const validate =
        () => {
            const nextErrors =
                {};


            if (
                !currentPassword
            ) {
                nextErrors.currentPassword =
                    t(
                        'memberedit.passwordChange.currentRequired'
                    );
            } else if (
                currentPasswordStatus !==
                'valid'
            ) {
                nextErrors.currentPassword =
                    t(
                        'memberedit.passwordVerification.mismatch'
                    );
            }


            if (
                !newPassword
            ) {
                nextErrors.newPassword =
                    t(
                        'memberedit.passwordChange.newRequired'
                    );
            } else if (
                !PASSWORD_REGEX.test(
                    newPassword,
                )
            ) {
                nextErrors.newPassword =
                    t(
                        'signup.validation.passwordInvalid'
                    );
            } else if (
                newPassword ===
                currentPassword
            ) {
                nextErrors.newPassword =
                    t(
                        'memberedit.passwordChange.samePassword'
                    );
            }


            if (
                !confirmPassword
            ) {
                nextErrors.confirmPassword =
                    t(
                        'memberedit.passwordChange.confirmRequired'
                    );
            } else if (
                newPassword !==
                confirmPassword
            ) {
                nextErrors.confirmPassword =
                    t(
                        'memberedit.passwordChange.mismatch'
                    );
            }


            setErrors(
                nextErrors,
            );


            return (
                Object.keys(
                    nextErrors,
                ).length ===
                0
            );
        };


    const handleSubmit =
        async (
            event,
        ) => {
            event.preventDefault();


            if (
                !validate()
            ) {
                return;
            }

            setIsSaving(
                true,
            );

            setErrors(
                {},
            );


            try {
                const verified =
                    await verifyCurrentPassword(
                        currentPassword,
                    );


                if (
                    !verified
                ) {
                    setCurrentPasswordStatus(
                        'invalid',
                    );

                    setErrors({
                        currentPassword:
                            t(
                                'memberedit.passwordVerification.mismatch',
                            ),
                    });

                    return;
                }

                await changePassword({
                    currentPassword,
                    newPassword,
                });


                resetModal();

                onChanged?.();
            } catch (
            error
            ) {
                console.error(
                    '[PasswordChangeModal] Failed to change password:',
                    error,
                );


                const errorCode =
                    error?.code ||
                    error?.message ||
                    '';


                if (
                    errorCode ===
                    'CURRENT_PASSWORD_NOT_MATCH'
                ) {
                    setCurrentPasswordStatus(
                        'invalid',
                    );

                    setErrors({
                        currentPassword:
                            t(
                                'memberedit.passwordVerification.mismatch'
                            ),
                    });

                    return;
                }

                setErrors({
                    submit:
                        t(
                            'memberedit.passwordChange.changeFailed'
                        ),
                });
            } finally {
                setIsSaving(
                    false,
                );
            }
        };


    if (
        !open
    ) {
        return null;
    }


    return createPortal(
        <div
            className="memberInfo-password-modal-overlay"
            role="presentation"
            onMouseDown={(
                event,
            ) => {
                if (
                    event.target ===
                    event.currentTarget
                ) {
                    handleClose();
                }
            }}
        >
            <form
                className="memberInfo-password-dialog"
                onSubmit={
                    handleSubmit
                }
            >
                <div
                    className="memberInfo-password-dialog-header"
                >
                    <div>
                        <h3>
                            {t(
                                'memberedit.passwordChange.title'
                            )}
                        </h3>

                        <p>
                            {t(
                                'memberedit.passwordChange.description'
                            )}
                        </p>
                    </div>

                    <button
                        type="button"
                        className="memberInfo-password-dialog-close"
                        onClick={
                            handleClose
                        }
                        disabled={
                            isSaving
                        }
                        aria-label={
                            t(
                                'sidebar.close'
                            )
                        }
                    >
                        ×
                    </button>
                </div>


                <div
                    className="memberInfo-password-dialog-content"
                >
                    <div
                        className="memberInfo-password-change-field"
                    >
                        <label
                            htmlFor="memberInfo-current-password"
                        >
                            {t(
                                'memberedit.passwordChange.currentPassword'
                            )}
                        </label>

                        <input
                            id="memberInfo-current-password"
                            type="password"
                            value={
                                currentPassword
                            }
                            onChange={(
                                event,
                            ) => {
                                setCurrentPassword(
                                    event.target.value,
                                );

                                clearSubmitError();
                            }}
                            autoComplete="current-password"
                            autoFocus
                            disabled={
                                isSaving
                            }
                        />


                        {currentPasswordStatus ===
                            'checking' && (
                                <div
                                    className="memberInfo-password-live-status is-checking"
                                >
                                </div>
                            )}


                        {currentPasswordStatus ===
                            'valid' && (
                                <div
                                    className="memberInfo-password-live-status is-valid"
                                >
                                </div>
                            )}


                        {errors.currentPassword &&
                            currentPasswordStatus !==
                            'checking' &&
                            currentPasswordStatus !==
                            'valid' && (
                                <div
                                    className="memberInfo-password-dialog-error"
                                >
                                    {
                                        errors.currentPassword
                                    }
                                </div>
                            )}
                    </div>


                    {/* 새 비밀번호 */}
                    <div
                        className="memberInfo-password-change-field"
                    >
                        <label
                            htmlFor="memberInfo-new-password"
                        >
                            {t(
                                'memberedit.passwordChange.newPassword'
                            )}
                        </label>

                        <input
                            id="memberInfo-new-password"
                            type="password"
                            value={
                                newPassword
                            }
                            maxLength={
                                12
                            }
                            onChange={(
                                event,
                            ) => {
                                setNewPassword(
                                    event.target.value,
                                );

                                clearSubmitError();

                                setErrors(
                                    (
                                        previous,
                                    ) => {
                                        const next = {
                                            ...previous,
                                        };

                                        delete next.newPassword;
                                        delete next.confirmPassword;

                                        return next;
                                    },
                                );
                            }}
                            autoComplete="new-password"
                            disabled={
                                isSaving
                            }
                        />


                        <ul
                            className="memberInfo-password-rule-list"
                        >
                            <li
                                className={
                                    newPassword
                                        ? passwordRules.format
                                            ? 'memberInfo-password-rule is-valid'
                                            : 'memberInfo-password-rule is-invalid'
                                        : 'memberInfo-password-rule'
                                }
                            >
                                <span>
                                    {newPassword
                                        ? passwordRules.format
                                            ? '✓'
                                            : '×'
                                        : '•'}
                                </span>

                                {t(
                                    'signup.validation.passwordInvalid'
                                )}
                            </li>

                            {newPassword &&
                                currentPassword &&
                                newPassword ===
                                currentPassword && (
                                    <li
                                        className="memberInfo-password-rule is-invalid"
                                    >
                                        <span>
                                            ×
                                        </span>

                                        {t(
                                            'memberedit.passwordChange.samePassword'
                                        )}
                                    </li>
                                )}
                        </ul>


                        {errors.newPassword && (
                            <div
                                className="memberInfo-password-dialog-error"
                            >
                                {
                                    errors.newPassword
                                }
                            </div>
                        )}
                    </div>

                    <div
                        className="memberInfo-password-change-field"
                    >
                        <label
                            htmlFor="memberInfo-confirm-password"
                        >
                            {t(
                                'memberedit.passwordChange.newPasswordConfirm'
                            )}
                        </label>

                        <input
                            id="memberInfo-confirm-password"
                            type="password"
                            value={
                                confirmPassword
                            }
                            maxLength={
                                12
                            }
                            onChange={(
                                event,
                            ) => {
                                setConfirmPassword(
                                    event.target.value,
                                );

                                clearSubmitError();

                                setErrors(
                                    (
                                        previous,
                                    ) => {
                                        const next = {
                                            ...previous,
                                        };

                                        delete next.confirmPassword;

                                        return next;
                                    },
                                );
                            }}
                            autoComplete="new-password"
                            disabled={
                                isSaving
                            }
                        />


                        {confirmPassword && (
                            <div
                                className={
                                    passwordRules.confirmation
                                        ? 'memberInfo-password-live-status is-valid'
                                        : 'memberInfo-password-live-status is-invalid'
                                }
                            >
                                {!passwordRules.confirmation &&
                                    t(
                                        'memberedit.passwordChange.mismatch'
                                    )}
                            </div>
                        )}


                        {errors.confirmPassword && (
                            <div
                                className="memberInfo-password-dialog-error"
                            >
                                {
                                    errors.confirmPassword
                                }
                            </div>
                        )}
                    </div>


                    {errors.submit && (
                        <div
                            className="memberInfo-password-dialog-error memberInfo-password-submit-error"
                        >
                            {
                                errors.submit
                            }
                        </div>
                    )}
                </div>


                <div
                    className="memberInfo-password-dialog-footer"
                >
                    <button
                        type="button"
                        className="memberInfo-cancel-button"
                        onClick={
                            handleClose
                        }
                        disabled={
                            isSaving
                        }
                    >
                        {t(
                            'common.cancel'
                        )}
                    </button>

                    <button
                        type="submit"
                        className="memberInfo-save-button"
                        disabled={
                            !canSave
                        }
                    >
                        {isSaving
                            ? t(
                                'common.saving'
                            )
                            : t(
                                'common.save'
                            )}
                    </button>
                </div>
            </form>
        </div>,
        document.body,
    );
}